import { useState } from 'react';
import { Navigation } from '../../components/Navigation';
import { FloatingBackButton } from '../../components/FloatingBackButton';
import { XPBar } from '../../components/gamification/XPBar';
import { BadgeDisplay } from '../../components/gamification/BadgeDisplay';
import { QuestDashboard } from '../../components/gamification/QuestDashboard';
import { Leaderboard } from '../../components/gamification/Leaderboard';
import { useAuthStore } from '../../store/authStore';
import { Trophy, Award, Target, TrendingUp, Flame } from 'lucide-react';

type Tab = 'overview' | 'quests' | 'badges' | 'leaderboard';

export function GamificationDashboard() {
  const { profile } = useAuthStore();
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  if (!profile) {
    return null;
  }

  const tabs: { id: Tab; label: string; icon: any }[] = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'quests', label: 'Quests', icon: Target },
    { id: 'badges', label: 'Badges', icon: Award },
    { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <FloatingBackButton />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
              <Trophy className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Your Progress</h1>
              <p className="text-gray-600">Level up your career journey</p>
            </div>
          </div>

          {/* XP Bar - Always Visible */}
          <div className="mt-6 bg-white rounded-xl p-6 shadow-sm border-2 border-gray-200">
            <XPBar
              currentXP={profile.xp || 0}
              currentLevel={profile.level || 1}
              showDetailed={true}
            />

            {/* Streak */}
            {profile.streak_days && profile.streak_days > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <Flame className="w-5 h-5 text-orange-500" />
                  <span className="font-bold text-gray-900">{profile.streak_days} Day Streak!</span>
                  <span className="text-sm text-gray-600">Keep it up!</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex gap-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 pb-4 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-semibold">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  icon={<Trophy className="w-6 h-6 text-yellow-500" />}
                  label="Current Level"
                  value={`Level ${profile.level || 1}`}
                  bgColor="from-yellow-50 to-amber-50"
                />
                <StatCard
                  icon={<Award className="w-6 h-6 text-purple-500" />}
                  label="Total XP"
                  value={(profile.xp || 0).toLocaleString()}
                  bgColor="from-purple-50 to-blue-50"
                />
                <StatCard
                  icon={<Target className="w-6 h-6 text-green-500" />}
                  label="Active Quests"
                  value="0"
                  bgColor="from-green-50 to-emerald-50"
                />
                <StatCard
                  icon={<Flame className="w-6 h-6 text-orange-500" />}
                  label="Login Streak"
                  value={`${profile.streak_days || 0} days`}
                  bgColor="from-orange-50 to-red-50"
                />
              </div>

              {/* Recent Badges */}
              <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-200">
                <BadgeDisplay limit={6} />
              </div>

              {/* Quick Quests Preview */}
              <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    Daily Quests
                  </h3>
                  <button
                    onClick={() => setActiveTab('quests')}
                    className="text-sm text-purple-600 font-medium hover:underline"
                  >
                    View All
                  </button>
                </div>
                <p className="text-gray-600">Check the Quests tab to see your active missions!</p>
              </div>
            </>
          )}

          {activeTab === 'quests' && (
            <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-200">
              <QuestDashboard />
            </div>
          )}

          {activeTab === 'badges' && (
            <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-200">
              <BadgeDisplay showAll />
            </div>
          )}

          {activeTab === 'leaderboard' && (
            <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-200">
              <Leaderboard />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  bgColor: string;
}

function StatCard({ icon, label, value, bgColor }: StatCardProps) {
  return (
    <div className={`rounded-xl p-6 bg-gradient-to-br ${bgColor} border-2 border-gray-200`}>
      <div className="flex items-center gap-3 mb-2">
        {icon}
        <span className="text-sm font-medium text-gray-600">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}
